import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { fetchGetCatalogosModificacionPrecios, ProductoCatalogoItem } from "../services/productoService";
import { useUserContext } from "./UserContext";

type ProveedoresContextType = {
  proveedores: ProductoCatalogoItem[];
  loading: boolean;
  refreshProveedores: () => Promise<void>;
};

const PROVEEDORES_STORAGE_KEY = "genfe_proveedores";

const ProveedoresContext = createContext<ProveedoresContextType | undefined>(undefined);

export const useProveedores = (): ProveedoresContextType => {
  const context = useContext(ProveedoresContext);
  if (!context) {
    throw new Error("useProveedores must be used within a ProveedoresProvider");
  }
  return context;
};

const readCachedProveedores = (): ProductoCatalogoItem[] => {
  const raw = sessionStorage.getItem(PROVEEDORES_STORAGE_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    sessionStorage.removeItem(PROVEEDORES_STORAGE_KEY);
    return [];
  }
};

export const ProveedoresProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { getUser } = useUserContext();
  const user = getUser();
  const [proveedores, setProveedores] = useState<ProductoCatalogoItem[]>(readCachedProveedores);
  const [loading, setLoading] = useState(false);
  const loadedTokenRef = useRef<string | undefined>(undefined);

  const loadProveedores = useCallback(async (forceRefresh: boolean) => {
    const currentUser = getUser();
    if (!currentUser || currentUser.idCliente) {
      setProveedores([]);
      sessionStorage.removeItem(PROVEEDORES_STORAGE_KEY);
      loadedTokenRef.current = undefined;
      return;
    }

    if (!forceRefresh && loadedTokenRef.current === currentUser.token) {
      return;
    }

    setLoading(true);
    try {
      const catalogos = await fetchGetCatalogosModificacionPrecios(currentUser);
      const nextProveedores = catalogos.proveedores ?? [];
      setProveedores(nextProveedores);
      loadedTokenRef.current = currentUser.token;
      sessionStorage.setItem(PROVEEDORES_STORAGE_KEY, JSON.stringify(nextProveedores));
    } finally {
      setLoading(false);
    }
  }, [getUser]);

  const refreshProveedores = useCallback(async () => {
    await loadProveedores(true);
  }, [loadProveedores]);

  useEffect(() => {
    if (user?.token && !user.idCliente) {
      loadProveedores(false).catch((error) => console.error("Error loading proveedores:", error));
    } else {
      setProveedores([]);
      loadedTokenRef.current = undefined;
      sessionStorage.removeItem(PROVEEDORES_STORAGE_KEY);
    }
  }, [user?.token, user?.idCliente, loadProveedores]);

  const value = useMemo(() => ({ proveedores, loading, refreshProveedores }), [proveedores, loading, refreshProveedores]);

  return (
    <ProveedoresContext.Provider value={value}>
      {children}
    </ProveedoresContext.Provider>
  );
};
